import React from "react";
import { Link } from "gatsby";
import PropTypes from "prop-types";
import { valueExists } from "../../../utils/valueExists/valueExists.util";

interface CustomLinkProps {
  children: React.ReactNode;
  url: string | undefined;
  linkType?: string;
  className?: string;
  style?: React.CSSProperties;
  activeClass?: string;
  isDisabled?: boolean;
  onMouseEnter?: Function;
  onMouseLeave?: Function;
  anchor?: any;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  id?: string;
  ariaLabel?: string;
}

const CustomLink: React.FC<CustomLinkProps> = ({
  children,
  url,
  linkType = undefined,
  className,
  style,
  activeClass,
  isDisabled = false,
  onMouseEnter = () => {},
  onMouseLeave = () => {},
  onClick = () => {},
  anchor,
  id,
  ariaLabel,
}) => {
  let path = url || "";
  const hasAnchor = anchor;

  const isEmail = (): boolean => {
    return (valueExists(linkType) && linkType === "email") || path?.startsWith("mailto:");
  };

  const isOutboundLink = /^https?:\/\//.test(path) || path?.startsWith("mailto:") || path?.startsWith("tel:");

  const getPath = (): string => {
    if (isEmail()) {
      return path.startsWith("mailto:") ? path : `mailto:${path}`;
    } else if (hasAnchor) {
      return `${path}#${anchor}`;
    } else {
      return path;
    }
  };

  const getRequiredTarget = (): string => {
    return isOutboundLink ? "_blank" : "";
  };

  if (isOutboundLink || isEmail()) {
    return (
      <a
        href={getPath()}
        target={getRequiredTarget()}
        rel="noreferrer noopener"
        {...(ariaLabel && { "aria-label": ariaLabel })}
        className={`custom-link ${className} ${isDisabled && "pointer-events-none"}`}
        style={style}
        onMouseEnter={() => onMouseEnter}
        onMouseLeave={() => onMouseLeave}
        onClick={onClick}
      >
        {children}
      </a>
    );
  }

  if (!path?.startsWith("/")) path = `/${path}`;

  if (path === "/home") path = "/";

  /*-- domain folder related stuff --*/
  const GATSBY_DOMAIN_FOLDER = process.env.GATSBY_DOMAIN_FOLDER;
  if (GATSBY_DOMAIN_FOLDER && path.includes(GATSBY_DOMAIN_FOLDER)) path = path.replace(`${GATSBY_DOMAIN_FOLDER}`, "");

  // Resolve the anchor from either the dedicated `anchor` prop or an inline hash in the URL
  // (e.g. an author typing "/school-partnerships#partners" straight into the link field).
  let hash = hasAnchor ? String(anchor) : "";
  if (!hash) {
    const hashIndex = path.indexOf("#");
    if (hashIndex !== -1) {
      hash = path.substring(hashIndex + 1);
      path = path.substring(0, hashIndex) || "/";
    }
  }

  if (path.charAt(path.length - 1) != "/" && !path.includes("?")) path = path + "/";
  path = path.toLowerCase();

  if (hash) {
    if (path.charAt(path.length - 1) === "/") path = path.substring(0, path.length - 1);
    path = `${path}#${hash}`;
  }

  // Same-page anchor (e.g. an author entered just "#contact-us"). Render a native
  // anchor so the browser handles fragment navigation and CSS smooth-scroll (see
  // global.css). Using a Gatsby <Link> here would send a relative link through
  // gatsby-link's trailing-slash normalisation, turning "#contact-us" into
  // "#contact-us/" and breaking the scroll target.
  if (path.startsWith("#")) {
    return (
      <a
        href={path}
        {...(ariaLabel && { "aria-label": ariaLabel })}
        className={`custom-link ${isDisabled && "pointer-events-none"} ${className}`}
        style={style}
        onMouseEnter={() => onMouseEnter}
        onMouseLeave={() => onMouseLeave}
        onClick={onClick}
        id={id}
      >
        {children}
      </a>
    );
  }

  return (
    <Link
      to={path}
      {...(ariaLabel && { "aria-label": ariaLabel })}
      className={`custom-link ${isDisabled && "pointer-events-none"} ${className}`}
      style={style}
      activeClassName={activeClass}
      onMouseEnter={() => onMouseEnter}
      onMouseLeave={() => onMouseLeave}
      onClick={onClick}
      id={id}
    >
      {children}
    </Link>
  );
};

CustomLink.defaultProps = {
  url: "",
};

CustomLink.propTypes = {
  url: PropTypes.string.isRequired,
};

export default CustomLink;
